import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { AuthContext } from './AuthContext';
import { Config, Device, HeaderConfig, Loadable } from '../types';
import { LoadableObject } from '../types/LoadableObject';
import { apiClient } from '../api';
import { resolvePendingInvitations } from '../api';

interface updateAppearanceType {
  header?: HeaderConfig;
  theme?: string;
  bg?: string;
}

interface DataContextType {
  config: LoadableObject<Config>;
  devices: Loadable<Device>;
  setDevices: (devices: Loadable<Device>) => void;
  reloadConfig: () => Promise<void>;
  reloadDevices: () => Promise<void>;
  updateAppearance: (appearance: updateAppearanceType) => Promise<void>;
}

const loadableDevices: Loadable<Device> = { loading: true };
const loadableConfig: LoadableObject<Config> = { loading: true };
const initialContext: DataContextType = {
  config: loadableConfig,
  devices: loadableDevices,
  setDevices: () => {},
  reloadConfig: () => Promise.resolve(),
  reloadDevices: () => Promise.resolve(),
  updateAppearance: () => Promise.resolve(),
};
const DataContext = createContext<DataContextType>(initialContext);

function DataProvider({ children }: { children: ReactNode }) {
  const { auth } = useContext(AuthContext);
  const [config, setConfig] = useState<LoadableObject<Config>>({ loading: true });
  const [devices, setDevices] = useState<Loadable<Device>>({ loading: true });

  useEffect(() => {
    if (auth && auth.token) {
      // Wait a short time to ensure a token is properly set in apiClient
      setTimeout(() => {
        Promise.all([reloadConfig(), handlePendingInvitations().then(reloadDevices)]).catch((err) =>
          console.error('Error loading initial data:', err),
        );
      }, 500);
    }
  }, [auth]);

  useEffect(() => {
    if (devices.error) console.log('Error loading devices', devices.error);
    if (config.error) console.log('Error loading config', config.error);
  }, [devices, config]);

  // Using the imported resolvePendingInvitations function from deviceService
  const handlePendingInvitations = () =>
    resolvePendingInvitations()
      .then(() => console.log('Pending invitations resolved'))
      .catch((err) => console.log('Error resolving pending invitations', err));

  const reloadConfig = async () => {
    setConfig(() => ({ loading: true }));
    try {
      const response = await apiClient.get('/api/config');
      setConfig({ loading: false, data: response.data });
      return console.log('Config successfully loaded');
    } catch (err) {
      return setConfig({ loading: false, error: err as Error });
    }
  };

  const reloadDevices = async () => {
    setDevices(() => ({ loading: true }));
    try {
      const response = await apiClient.get('/api/devices');
      setDevices({ loading: false, data: response.data });
      return console.log('Devices successfully loaded');
    } catch (err) {
      return setDevices({ loading: false, error: err as Error });
    }
  };

  const updateAppearance = async (appearance: updateAppearanceType): Promise<void> => {
    await apiClient.post('/api/config/appearance', appearance);
    setConfig({ ...config, data: { ...(config.data as any), ...appearance } });
  };

  return (
    <DataContext.Provider
      value={{
        config,
        devices,
        setDevices,
        reloadDevices,
        reloadConfig,
        updateAppearance,
      }}
    >
      {children}
    </DataContext.Provider>
  );
}

export { DataContext, DataProvider };
